<template>
  <div class="user-benefits-tab">
    <div v-if="isLoading" class="text-center py-8">
      <i class="pi pi-spin pi-spinner text-2xl text-gray-400" />
      <p class="text-gray-500 mt-2">Loading benefits...</p>
    </div>

    <div v-else-if="error" class="text-center py-8">
      <p class="text-red-500">Failed to load benefits</p>
    </div>

    <div v-else-if="userBenefits.length === 0" class="text-center py-12">
      <i class="pi pi-gift text-4xl text-gray-300 mb-4" />
      <p class="text-gray-500 text-lg font-medium">No benefits found</p>
      <p class="text-gray-400 text-sm mt-2">
        This member doesn't have any active benefits yet
      </p>
      <Button
        v-if="isPlayerProfile"
        label="Add Benefit"
        icon="pi pi-plus"
        class="mt-4"
        @click="openAddDrawer"
      />
    </div>

    <div v-else class="space-y-6">
      <div v-if="isPlayerProfile" class="flex justify-end">
        <Button
          label="Add Benefit"
          icon="pi pi-plus"
          size="small"
          @click="openAddDrawer"
        />
      </div>

      <DataTable :value="userBenefits" striped-rows row-hover class="text-sm">
        <Column field="source_membership_title" header="Offering" sortable />

        <Column
          v-if="showPlayerColumn"
          field="profile.name"
          header="Player"
          sortable
        >
          <template #body="{ data }">
            <span v-if="data.profile?.name">{{ data.profile.name }}</span>
            <span v-else class="text-gray-400">-</span>
          </template>
        </Column>

        <Column field="service_summaries" header="Service" sortable>
          <template #body="{ data }">
            <BenefitServicesCell :service-summaries="data.service_summaries" />
          </template>
        </Column>

        <Column field="discount_percentage" header="Discount" sortable>
          <template #body="{ data }">
            {{ formatDiscount(data.discount_percentage) }}
          </template>
        </Column>

        <Column field="usage" header="Usage" sortable>
          <template #body="{ data }">
            <span v-if="data.limit_type === LimitTypeEnum.unlimited">
              {{ data.quantity_used || 0 }} used
            </span>
            <span v-else>
              {{ data.quantity_used || 0 }} / {{ data.quantity_total || 0 }}
            </span>
          </template>
        </Column>

        <Column field="limit_type" header="Type" sortable>
          <template #body="{ data }">
            {{ formatLimitTypeDisplay(data) }}
          </template>
        </Column>

        <Column field="expires_at" header="Expires" sortable>
          <template #body="{ data }">
            <span v-if="data.expires_at" class="text-gray-700">
              {{ getFormattedDate(data.expires_at) }}
            </span>
            <span v-else class="text-gray-500">-</span>
          </template>
        </Column>

        <Column field="is_frozen" header="Status" sortable>
          <template #body="{ data }">
            <Tag
              :value="getBenefitStatus(data)"
              :severity="getBenefitStatusSeverity(data)"
            />
          </template>
        </Column>

        <Column
          field="source_membership"
          header="Source"
          sortable
          :style="{ minWidth: '12rem' }"
        >
          <template #body="{ data }">
            <span
              v-if="data.source === BenefitSourceEnum.admin"
              class="text-gray-500 text-sm"
            >
              Added by admin
            </span>
            <Button
              v-else
              :label="getSourceLabel(data)"
              text
              size="small"
              icon="pi pi-external-link"
              class="!whitespace-nowrap"
              @click="navigateToSource(data)"
            />
          </template>
        </Column>

        <Column v-if="isPlayerProfile" header="" :style="{ width: '3rem' }">
          <template #body="{ data }">
            <Button
              v-if="data.source === BenefitSourceEnum.admin"
              icon="pi pi-ellipsis-v"
              text
              rounded
              size="small"
              :aria-label="`Actions for ${getBenefitServiceLabel(data)}`"
              @click="toggleMenu($event, data)"
            />
          </template>
        </Column>
      </DataTable>
    </div>

    <Menu ref="menuRef" :model="menuItems" :popup="true" />

    <ProfileBenefitDrawer
      v-model:visible="isDrawerVisible"
      :edit-mode="isEditMode"
      :initial-data="selectedBenefit ?? undefined"
      :profile-id="profileId"
      @saved="onBenefitSaved"
    />

    <DeleteBenefitModal
      v-model:visible="isDeleteModalVisible"
      :limit-type="selectedBenefit?.limit_type"
      :benefit-name="
        selectedBenefit ? getBenefitServiceLabel(selectedBenefit) : undefined
      "
      @confirm="onDeleteConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Tag from "primevue/tag";
import Menu from "primevue/menu";
import {
  formatDiscount,
  getBenefitStatus,
  getBenefitStatusSeverity,
  formatRenewalCadence,
  getBenefitServiceLabel,
} from "@/modules/benefits/helpers";
import { getFormattedDate } from "@/shared/utils/helpers";
import { LimitTypeEnum, BenefitSourceEnum } from "@/api/model";
import type { AdminSubscriberBenefit } from "@/api/model";
import { getProfileBenefits } from "@/modules/benefits/api";
import { useRouter } from "vue-router";
import { ROUTE_NAME } from "@/shared/constants/routes";
import ProfileBenefitDrawer from "@/modules/benefits/components/ProfileBenefitDrawer.vue";
import DeleteBenefitModal from "@/modules/benefits/components/DeleteBenefitModal.vue";
import BenefitServicesCell from "@/modules/benefits/components/BenefitServicesCell.vue";
import { useManualBenefit } from "@/modules/benefits/composables/useManualBenefit";

interface Props {
  profileId: number;
  userRoles?: string[];
}

const props = defineProps<Props>();
const router = useRouter();
const { remove } = useManualBenefit();

const userBenefits = ref<AdminSubscriberBenefit[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);

const isDrawerVisible = ref(false);
const isEditMode = ref(false);
const selectedBenefit = ref<AdminSubscriberBenefit | null>(null);
const isDeleteModalVisible = ref(false);
const menuRef = ref();

const isPlayerProfile = computed(() => {
  return props.userRoles?.includes("player") || false;
});

const showPlayerColumn = computed(() => {
  return props.userRoles?.includes("contact") || false;
});

const menuItems = computed(() => [
  {
    label: "Edit Benefit",
    icon: "pi pi-pencil",
    command: () => {
      if (selectedBenefit.value) openEditDrawer(selectedBenefit.value);
    },
  },
  {
    label: "Delete Benefit",
    icon: "pi pi-trash",
    class: "text-red-600",
    command: () => {
      isDeleteModalVisible.value = true;
    },
  },
]);

onMounted(async () => {
  await fetchBenefits();
});

async function fetchBenefits() {
  isLoading.value = true;
  error.value = null;

  try {
    const result = await getProfileBenefits(props.profileId);

    if ("error" in result) {
      error.value = result.error;
      return;
    }

    // API returns a direct array of benefits
    userBenefits.value = result;
  } catch (err) {
    error.value = "Failed to fetch benefits";
    console.error("Fetch benefits error:", err);
  } finally {
    isLoading.value = false;
  }
}

function formatLimitTypeDisplay(benefit: AdminSubscriberBenefit): string {
  const limitType = benefit.limit_type;

  if (limitType === LimitTypeEnum.unlimited) {
    return "Unlimited";
  }

  if (limitType === LimitTypeEnum.fixed) {
    return "Fixed";
  }

  if (limitType === LimitTypeEnum.recurring) {
    const cadenceLabel = formatRenewalCadence(benefit.renewal_cadence);
    return cadenceLabel ? `Recurring (${cadenceLabel})` : "Recurring";
  }

  return limitType;
}

function getSourceLabel(benefit: AdminSubscriberBenefit): string {
  return benefit.source_offering_type === "registration"
    ? "View Registration"
    : "View Membership";
}

function navigateToSource(benefit: AdminSubscriberBenefit) {
  const routeName =
    benefit.source_offering_type === "registration"
      ? ROUTE_NAME.PAYMENT_REGISTRATIONS_VIEW
      : ROUTE_NAME.PAYMENT_MEMBERSHIPS_VIEW;

  router.push({
    name: routeName,
    params: { id: benefit.source_membership },
  });
}

function toggleMenu(event: Event, benefit: AdminSubscriberBenefit) {
  selectedBenefit.value = benefit;
  menuRef.value.toggle(event);
}

function openAddDrawer() {
  isEditMode.value = false;
  selectedBenefit.value = null;
  isDrawerVisible.value = true;
}

function openEditDrawer(benefit: AdminSubscriberBenefit) {
  isEditMode.value = true;
  selectedBenefit.value = benefit;
  isDrawerVisible.value = true;
}

async function onBenefitSaved() {
  await fetchBenefits();
}

async function onDeleteConfirm() {
  if (!selectedBenefit.value) return;

  const success = await remove(props.profileId, selectedBenefit.value.id);

  if (success) {
    await fetchBenefits();
    selectedBenefit.value = null;
  }
}
</script>
